import { lazy, Suspense, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { DealMediaBadges } from '@/components/DealMediaBadges';

import './DealGallery.css';

// Lazy-load the lightbox subcomponent so the catalogue chunk doesn't pay the
// ~18 KB cost on initial render. Bundle is fetched on the first thumb click.
const LazyGalleryLightbox = lazy(() => import('./GalleryLightbox'));

export type GalleryPhoto = { url: string; alt_text: string };

export type DealGalleryProps = {
  photos: GalleryPhoto[];
  /** Used as a fallback alt-text and for the open-lightbox aria-label. */
  dealTitle: string;
  /** Positive number representing the percentage saved vs reference_price
   * (e.g. `19.35` for a 19.35% discount). Rendered in the hero overlay only
   * when the scope-aware mode warrants it (see below). */
  discountPct?: number;
  /** Persisted scope-aware confidence + scope (ADR-047). Gate the hero overlay:
   * the bronze badge shows only for building / medium-community / off-plan
   * (developer) modes — a low-confidence community context benchmark gets no
   * prominent hero badge (the nuance lives in the BenchmarkPanel below). */
  dldConfidence?: string | null;
  dldScope?: string | null;
  /** Property kind (apartment/villa/…) for the top-left badge on the main photo. */
  propertyType?: string | null;
};

// ADR-015 — 1 hero + 2 thumbs visible (dérogation Spec V1.1 §M4 L610 + UB
// §5.3 L825 qui figent 3 thumbs). La photo 4+ reste atteignable 100% via la
// lightbox ouverte au clic n'importe où dans la grille.
const VISIBLE_THUMB_COUNT = 2;

// `fetchpriority` is a recent HTML attribute; React's JSX types lag behind.
// We cast the literal once instead of `as any` at every usage site so the
// override stays auditable.
const HERO_IMG_PRIORITY = { fetchpriority: 'high' } as unknown as { fetchPriority?: 'high' };

// 24×24 Feather/Lucide camera glyph — `currentColor` so the badge controls
// the tint via CSS. No icon library shipped in V1, custom inline SVG follows
// the project pattern (cf. `<WhatsAppIcon>` in `FloatingCTAActions/`).
function CameraIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

export function DealGallery({
  photos,
  dealTitle,
  discountPct,
  dldConfidence,
  dldScope,
  propertyType,
}: DealGalleryProps) {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (photos.length === 0) {
    return (
      <div className="deal-gallery__inner">
        <div className="deal-gallery__grid deal-gallery__grid--thumbs-0">
          <div className="deal-gallery__main deal-gallery__main--empty">
            <ImagePlaceholder
              label={t('deal.gallery.empty')}
              style={{ height: '100%' } as CSSProperties}
            />
          </div>
        </div>
      </div>
    );
  }

  const hero = photos[0];
  const thumbs = photos.slice(1, 1 + VISIBLE_THUMB_COUNT);
  const slides = photos.map((p) => ({ src: p.url, alt: p.alt_text }));
  // Badge `[icon] N` always shown when there's more than 1 photo. Anchored at
  // the grid bottom-right via CSS — sits visually on the last visible thumb
  // on desktop and on the hero on mobile (where thumbs are display:none).
  const showCountBadge = photos.length >= 2;

  return (
    <div className="deal-gallery__inner">
      <div className={`deal-gallery__grid deal-gallery__grid--thumbs-${thumbs.length}`}>
        <button
          type="button"
          className="deal-gallery__main"
          onClick={() => setOpenIndex(0)}
          aria-label={t('deal.gallery.openLightboxAria', { title: dealTitle })}
        >
          <img
            src={hero.url}
            alt={hero.alt_text || dealTitle}
            loading="eager"
            decoding="async"
            className="deal-gallery__img"
            {...HERO_IMG_PRIORITY}
          />
          <DealMediaBadges
            propertyType={propertyType}
            discountPct={discountPct}
            dldConfidence={dldConfidence}
            dldScope={dldScope}
            size="feature"
          />
        </button>

        {thumbs.map((photo, idx) => {
          const galleryIndex = idx + 1; // photos[0] is the hero
          return (
            <button
              key={photo.url}
              type="button"
              className="deal-gallery__thumb"
              onClick={() => setOpenIndex(galleryIndex)}
              aria-label={t('deal.gallery.thumbAria', {
                index: galleryIndex + 1,
                total: photos.length,
              })}
            >
              <img
                src={photo.url}
                alt={photo.alt_text || dealTitle}
                loading="lazy"
                decoding="async"
                className="deal-gallery__img"
              />
            </button>
          );
        })}

        {showCountBadge ? (
          <span
            className="deal-gallery__count-badge"
            data-testid="deal-gallery-count-badge"
            aria-hidden="true"
          >
            <CameraIcon />
            <span className="deal-gallery__count-badge__num">{photos.length}</span>
          </span>
        ) : null}
      </div>

      {openIndex !== null ? (
        <Suspense fallback={null}>
          <LazyGalleryLightbox
            open={openIndex !== null}
            index={openIndex}
            slides={slides}
            close={() => setOpenIndex(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
